import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  PendingQuickAction,
  ModificationPreview,
  QuickActionPayment,
  QuickActionPaymentMethod,
} from '../../types';
import { addDays, formatDateShort } from '../../utils/dateUtils';
import './ReservationQuickActionPopover.css';

interface ReservationQuickActionPopoverProps {
  action: PendingQuickAction;
  anchorRect: DOMRect;
  onLoadPreview: (action: PendingQuickAction) => Promise<ModificationPreview>;
  onConfirm: (action: PendingQuickAction, reason: string, payment: QuickActionPayment) => Promise<void>;
  onCancel: () => void;
}

// Same set the modification wizard offers, plus an explicit "collect later" escape hatch.
const PAYMENT_METHODS: { value: QuickActionPaymentMethod; label: string; status: 'paid' | 'pending' }[] = [
  { value: 'payment_link', label: 'Payment link', status: 'pending' },
  { value: 'cash', label: 'Cash', status: 'paid' },
  { value: 'check', label: 'Check', status: 'paid' },
  { value: 'card_terminal', label: 'Card terminal', status: 'paid' },
  { value: 'none', label: 'Collect later', status: 'pending' },
];

// formatDateShort parses with `new Date(string)`, which treats a bare YYYY-MM-DD as UTC midnight
// and can roll back a day when displayed in a timezone behind UTC — append noon to avoid that.
const formatDateShortLocal = (dateString: string): string => formatDateShort(`${dateString}T12:00:00`);

const describeAction = (action: PendingQuickAction): string => {
  const addedNights = action.targetDays - action.reservation.days;
  const nightsLabel = `${addedNights > 0 ? '+' : ''}${addedNights} night${Math.abs(addedNights) === 1 ? '' : 's'}`;
  const newCheckOut = addDays(action.targetCheckIn, action.targetDays);

  switch (action.type) {
    case 'extend-checkin':
      return `Extend check-in to ${formatDateShortLocal(action.targetCheckIn)} (${nightsLabel})`;
    case 'extend-checkout':
      return `Extend checkout to ${formatDateShortLocal(newCheckOut)} (${nightsLabel})`;
    case 'move-site':
      return `Move to site ${action.targetSiteAlias}`;
    case 'move-dates':
      return `Move to ${formatDateShortLocal(action.targetCheckIn)} – ${formatDateShortLocal(newCheckOut)}`;
    case 'move-site-and-dates':
      return `Move to site ${action.targetSiteAlias}, ${formatDateShortLocal(action.targetCheckIn)} – ${formatDateShortLocal(newCheckOut)}`;
    default:
      return 'Modify reservation';
  }
};

const ReservationQuickActionPopover: React.FC<ReservationQuickActionPopoverProps> = ({
  action,
  anchorRect,
  onLoadPreview,
  onConfirm,
  onCancel,
}) => {
  const [preview, setPreview] = useState<ModificationPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<QuickActionPaymentMethod>('payment_link');
  const [paymentStatus, setPaymentStatus] = useState<'paid' | 'pending'>('pending');
  const [terminalTransactionId, setTerminalTransactionId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPreview(null);
    // A transaction ID belongs to the action it was typed for — never carry it over.
    setTerminalTransactionId('');
    onLoadPreview(action)
      .then(result => {
        if (!cancelled) setPreview(result);
      })
      .catch(err => {
        if (!cancelled) setError(err.message || 'Failed to load preview');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action]);

  const costDifference = preview?.cost_difference ?? 0;
  // Payment is only collected when the change actually adds a charge.
  const needsPayment = costDifference > 0;
  const terminalIdMissing =
    needsPayment && paymentMethod === 'card_terminal' && !terminalTransactionId.trim();

  const handleMethodChange = useCallback((method: QuickActionPaymentMethod) => {
    setPaymentMethod(method);
    setPaymentStatus(PAYMENT_METHODS.find(m => m.value === method)?.status ?? 'pending');
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!reason.trim() || terminalIdMissing) return;
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(action, reason.trim(), {
        method: needsPayment ? paymentMethod : 'none',
        status: paymentStatus,
        terminalTransactionId: terminalTransactionId.trim(),
      });
    } catch (err: any) {
      setError(err.message || 'Failed to apply change');
      setSubmitting(false);
    }
  }, [
    action,
    reason,
    needsPayment,
    paymentMethod,
    paymentStatus,
    terminalTransactionId,
    terminalIdMissing,
    onConfirm,
  ]);

  // Position near the anchor, clamped to stay within the viewport
  const style = (() => {
    const width = 320;
    const top = Math.min(anchorRect.bottom + 6, window.innerHeight - 40);
    const left = Math.min(Math.max(8, anchorRect.left), window.innerWidth - width - 8);
    return { top, left, width };
  })();

  return (
    <>
      <div className="quick-action-popover-backdrop" onClick={onCancel} />
      <div className="quick-action-popover" style={style} ref={popoverRef}>
        <div className="quick-action-popover-title">{describeAction(action)}</div>

        {loading && <div className="quick-action-popover-loading">Loading cost preview…</div>}

        {!loading && error && <div className="quick-action-popover-error">{error}</div>}

        {!loading && preview && (
          <div className="quick-action-popover-cost">
            <div className="quick-action-cost-row">
              <span>Cost difference</span>
              <span className={costDifference > 0 ? 'cost-positive' : costDifference < 0 ? 'cost-negative' : ''}>
                {costDifference > 0 ? '+' : ''}${costDifference.toFixed(2)}
              </span>
            </div>
            {preview.modification_fee > 0 && (
              <div className="quick-action-cost-row">
                <span>Modification fee</span>
                <span>${preview.modification_fee.toFixed(2)}</span>
              </div>
            )}
          </div>
        )}

        <label className="quick-action-popover-label">
          Reason <span className="required">*</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason for this change"
            rows={2}
          />
        </label>

        {needsPayment && (
          <div className="quick-action-payment">
            <div className="quick-action-payment-title">Collect ${costDifference.toFixed(2)} via</div>
            <div className="quick-action-payment-methods">
              {PAYMENT_METHODS.map(({ value, label }) => (
                <label key={value} className="quick-action-payment-option">
                  <input
                    type="radio"
                    name="quickActionPaymentMethod"
                    value={value}
                    checked={paymentMethod === value}
                    onChange={() => handleMethodChange(value)}
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>

            {(paymentMethod === 'cash' || paymentMethod === 'check') && (
              <label className="quick-action-popover-label">
                Payment status
                <select
                  value={paymentStatus}
                  onChange={(e) => setPaymentStatus(e.target.value as 'paid' | 'pending')}
                >
                  <option value="paid">Paid now</option>
                  <option value="pending">Pay at check-in</option>
                </select>
              </label>
            )}

            {paymentMethod === 'card_terminal' && (
              <label className="quick-action-popover-label">
                Transaction ID <span className="required">*</span>
                <input
                  type="text"
                  value={terminalTransactionId}
                  onChange={(e) => setTerminalTransactionId(e.target.value)}
                  placeholder="From terminal receipt"
                />
              </label>
            )}

            {paymentMethod === 'none' && (
              <div className="quick-action-payment-note">
                Balance stays due on the reservation — no payment recorded, no link sent.
              </div>
            )}
          </div>
        )}

        <div className="quick-action-popover-actions">
          <button className="btn btn-secondary" onClick={onCancel} disabled={submitting}>
            Cancel
          </button>
          <button
            className="btn btn-success"
            onClick={handleConfirm}
            disabled={!reason.trim() || terminalIdMissing || loading || !preview || submitting}
          >
            {submitting ? 'Applying…' : 'Confirm'}
          </button>
        </div>
      </div>
    </>
  );
};

export default ReservationQuickActionPopover;
